import { extractOutline, type OutlineItem } from './outline'

export interface SluggedOutlineItem extends OutlineItem {
  /** Stable text-derived id: "My title is this" -> "my-title-is-this".
   *  Duplicate slugs in document order get "-2", "-3"... suffixes. */
  slug: string
}

export interface HeadingSection extends SluggedOutlineItem {
  /** Position in outline order (0-based). */
  index: number
  /** Exclusive end: line index where the next same-or-higher-level heading
   *  starts, or the total line count. The section body spans
   *  (line, endLine) exclusive of the heading line itself. */
  endLine: number
  /** Outline index of the nearest preceding heading with a lower level,
   *  or null when the section sits at document root. */
  parentIndex: number | null
  /** True when the body contains any non-whitespace line. Empty sections
   *  show no fold chevron (nothing to hide). */
  hasBody: boolean
}

// Mirrored 1:1 by HeadingSections.swift in CaliuCore. Change both or neither.
// NFC-normalize first so composed/decomposed accents slug identically, then
// keep Unicode letters/marks/numbers/hyphens and dash everything else.
const NON_SLUG_RUN = /[^\p{L}\p{M}\p{N}-]+/gu

/**
 * Slug for a single heading's text (no occurrence disambiguation - that
 * needs the whole document; see outlineWithSlugs).
 */
export function slugifyHeading(text: string): string {
  const slug = text
    .normalize('NFC')
    .toLowerCase()
    .replace(NON_SLUG_RUN, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'section'
}

/**
 * extractOutline + occurrence-disambiguated slugs. The n-th duplicate gets
 * "-n" appended, probing upward until the candidate is unused so an organic
 * "intro-2" heading never collides with a generated one.
 */
export function outlineWithSlugs(content: string): SluggedOutlineItem[] {
  const items = extractOutline(content)
  const used = new Set<string>()
  return items.map((item) => {
    const base = slugifyHeading(item.text)
    let slug = base
    let n = 2
    while (used.has(slug)) {
      slug = `${base}-${n}`
      n += 1
    }
    used.add(slug)
    return { ...item, slug }
  })
}

/**
 * The full section model: every outline heading with its body range,
 * parent, and foldability. This is the shared contract behind fold ranges,
 * chevron placement, drag targets, and heading-link resolution.
 */
export function computeHeadingSections(content: string): HeadingSection[] {
  const lines = content.split('\n')
  const outline = outlineWithSlugs(content)

  return outline.map((item, index) => {
    let endLine = lines.length
    for (let j = index + 1; j < outline.length; j++) {
      if (outline[j].level <= item.level) {
        endLine = outline[j].line
        break
      }
    }

    let parentIndex: number | null = null
    for (let j = index - 1; j >= 0; j--) {
      if (outline[j].level < item.level) {
        parentIndex = j
        break
      }
    }

    let hasBody = false
    for (let i = item.line + 1; i < endLine; i++) {
      if (lines[i].trim()) {
        hasBody = true
        break
      }
    }

    return { ...item, index, endLine, parentIndex, hasBody }
  })
}

/**
 * Indexes of the contiguous run of same-level sections sharing the same
 * parent as sections[index] - the only legal drag-reorder positions for it.
 */
export function sectionSiblings(sections: HeadingSection[], index: number): number[] {
  const target = sections[index]
  if (!target) return []
  const siblings: number[] = []

  const isSibling = (s: HeadingSection) =>
    s.level === target.level && s.parentIndex === target.parentIndex

  // Walk left from index, then right, keeping only the contiguous run:
  // a section between two siblings that is deeper belongs to a sibling's
  // body (fine); one that is shallower breaks the run.
  let first = index
  for (let j = index - 1; j >= 0; j--) {
    if (sections[j].level < target.level) break
    if (isSibling(sections[j])) first = j
  }
  let last = index
  for (let j = index + 1; j < sections.length; j++) {
    if (sections[j].level < target.level) break
    if (isSibling(sections[j])) last = j
  }
  for (let j = first; j <= last; j++) {
    if (isSibling(sections[j])) siblings.push(j)
  }
  return siblings
}

/**
 * Outline indexes of every folded ancestor chain member that hides the
 * given heading, i.e. the sections to expand before scrolling to it.
 */
export function foldedAncestors(
  sections: HeadingSection[],
  index: number,
  folded: ReadonlySet<string>,
): number[] {
  const result: number[] = []
  let current = sections[index]?.parentIndex ?? null
  while (current !== null) {
    const section = sections[current]
    if (folded.has(section.slug)) result.push(current)
    current = section.parentIndex
  }
  return result.reverse()
}
